import {
  createContext,
  type PropsWithChildren,
  type SyntheticEvent,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';
import {
  ACCOUNT_FETCH_PAGE_SIZE,
  ACCOUNT_PAGE_SIZE,
  type AccountGroupValue,
  DEFAULT_ACCOUNT_GROUP
} from '../constants';
import type { AccountModel, AccountModelInput } from '@/types/schemas';
import { toast, useCRUD, useExcel, useExcelProcessor } from '@/hooks';
import { getPermissionResourceKeyFromPath } from '@/lib/permissions';
import { ColumnMapping, ValidationError } from '@/lib/excel';
import { toast as hotToast } from '@/lib/hot-toast';
import { ExcelConfig, ImportBody } from '../utils';
import { useAuth } from '@/contexts/auth-context';
import { resolveAccountGroup } from '../helpers';
import { QUERY_KEYS } from '@/constants';
import api from '@/lib/api';

type FormMode = 'add' | 'edit' | 'view';

interface PageContextValue {
  isLoading: boolean;
  rows: AccountModel[];
  filteredRows: AccountModel[];
  paginatedRows: AccountModel[];
  activeGroup: AccountGroupValue;
  selectedRowId: string | null;
  selectedRow: AccountModel | null;
  currentPage: number;
  pageSize: number;
  totalItems: number;
  formMode: FormMode;
  isFormOpen: boolean;
  isDeleteOpen: boolean;
  isBulkDeleteOpen: boolean;
  isDeleting: boolean;
  isCopyMode: boolean;
  formAccount: AccountModel | null;
  isRowActionDisabled: boolean;
  isDeleteActionDisabled: boolean;
  handleGroupChange: (group: AccountGroupValue) => void;
  handlePageChange: (page: number) => void;
  handleRowClick: (params: { id: string; row: AccountModel }, event?: SyntheticEvent | unknown) => Promise<void>;
  handleRefresh: () => Promise<void>;
  handleOpenAdd: () => void;
  handleOpenView: () => Promise<void>;
  handleOpenEdit: () => Promise<void>;
  handleOpenCopy: () => Promise<void>;
  handleRequestDelete: () => void;
  handleCloseDelete: () => void;
  handleConfirmDelete: () => Promise<void>;
  handleOpenBulkDelete: () => void;
  handleCloseBulkDelete: () => void;
  handleDeleteByUuid: (uuid: string) => Promise<void>;
  handleBulkDelete: (uuids: string[]) => Promise<void>;
  handleCloseForm: () => void;
  handleFormSuccess: () => Promise<void>;
  isImportDialogOpen: boolean;
  setIsImportDialogOpen: (open: boolean) => void;
  showErrorDialog: boolean;
  setShowErrorDialog: (show: boolean) => void;
  errorData: { errors: ValidationError[]; columnMapping: Record<string, ColumnMapping> } | null;
  handleImportFromExcel: (data: { errors?: string; file: File | null; sheetIndex?: string }) => Promise<void>;
  handleOpenImportExcel: () => void;
}

const AccountPageContext = createContext<PageContextValue | null>(null);

export function AccountPageProvider({ children }: PropsWithChildren) {
  const { entity, permissions, isAdmin } = useAuth();
  const {
    data: accounts,
    isLoading,
    fetchDetail,
    refreshData
  } = useCRUD<AccountModel, AccountModelInput>({
    endpoint: QUERY_KEYS.TAI_KHOAN_ALL,
    pageSize: ACCOUNT_FETCH_PAGE_SIZE
  });
  const { addItem: addAccount } = useCRUD<AccountModel, AccountModelInput>({
    endpoint: QUERY_KEYS.TAI_KHOAN,
    initialFetch: false
  });

  const { processFile } = useExcelProcessor(ExcelConfig);
  const { getUUIDByCode } = useExcel();

  const [activeGroup, setActiveGroup] = useState<AccountGroupValue>(DEFAULT_ACCOUNT_GROUP);
  const [selectedRowId, setSelectedRowId] = useState<string | null>(null);
  const [selectedRow, setSelectedRow] = useState<AccountModel | null>(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [formMode, setFormMode] = useState<FormMode>('add');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isBulkDeleteOpen, setIsBulkDeleteOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isCopyMode, setIsCopyMode] = useState(false);
  const [formAccount, setFormAccount] = useState<AccountModel | null>(null);

  const [isImportDialogOpen, setIsImportDialogOpen] = useState(false);
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [errorData, setErrorData] = useState<{
    errors: ValidationError[];
    columnMapping: Record<string, ColumnMapping>;
  } | null>(null);

  const rows = useMemo(() => accounts, [accounts]);
  const filteredRows = useMemo(
    () => rows.filter(row => resolveAccountGroup(row.code) === activeGroup),
    [activeGroup, rows]
  );
  const totalItems = filteredRows.length;
  const paginatedRows = useMemo(() => {
    const start = currentPage * ACCOUNT_PAGE_SIZE;
    return filteredRows.slice(start, start + ACCOUNT_PAGE_SIZE);
  }, [currentPage, filteredRows]);
  const isRowActionDisabled = !selectedRow;
  const isDeleteActionDisabled = !selectedRow;

  useEffect(() => {
    const lastPage = Math.max(Math.ceil(totalItems / ACCOUNT_PAGE_SIZE) - 1, 0);
    if (currentPage > lastPage) {
      setCurrentPage(lastPage);
    }
  }, [currentPage, totalItems]);

  const clearSelection = useCallback(() => {
    setSelectedRowId(null);
    setSelectedRow(null);
  }, []);

  useEffect(() => {
    const handleGlobalShortcut = (event: KeyboardEvent) => {
      if (!event.ctrlKey || event.key.toLowerCase() !== 'd') {
        return;
      }

      if (isFormOpen || isDeleteOpen || isBulkDeleteOpen || filteredRows.length === 0) {
        return;
      }

      event.preventDefault();
      setIsBulkDeleteOpen(true);
    };

    window.addEventListener('keydown', handleGlobalShortcut);
    return () => window.removeEventListener('keydown', handleGlobalShortcut);
  }, [filteredRows.length, isBulkDeleteOpen, isDeleteOpen, isFormOpen]);

  const resolveSelectedDetail = useCallback(async () => {
    if (!selectedRow?.uuid) {
      return null;
    }

    const detail = await fetchDetail(selectedRow.uuid);
    return Array.isArray(detail) ? null : detail;
  }, [fetchDetail, selectedRow?.uuid]);

  const handleDeleteByUuid = useCallback(
    async (uuid: string) => {
      if (!entity?.slug || !uuid) {
        throw new Error('Entity slug hoặc uuid không hợp lệ');
      }

      await api.delete(`/entities/${entity.slug}/erp/${QUERY_KEYS.TAI_KHOAN}/${uuid}/`);
    },
    [entity?.slug]
  );

  const handleGroupChange = useCallback(
    (group: AccountGroupValue) => {
      setActiveGroup(group);
      setCurrentPage(0);
      clearSelection();
    },
    [clearSelection]
  );

  const handlePageChange = useCallback(
    (page: number) => {
      setCurrentPage(page);
      clearSelection();
    },
    [clearSelection]
  );

  const handleRowClick = useCallback(async (params: { id: string; row: AccountModel }) => {
    setSelectedRowId(params.id);
    setSelectedRow(params.row);
  }, []);

  const handleOpenAdd = useCallback(() => {
    setFormMode('add');
    setIsCopyMode(false);
    setFormAccount(null);
    setIsFormOpen(true);
  }, []);

  const handleOpenView = useCallback(async () => {
    const detail = await resolveSelectedDetail();
    if (!detail) return;

    setFormMode('view');
    setIsCopyMode(false);
    setFormAccount(detail);
    setIsFormOpen(true);
  }, [resolveSelectedDetail]);

  const handleOpenEdit = useCallback(async () => {
    const detail = await resolveSelectedDetail();
    if (!detail) return;

    setFormMode('edit');
    setIsCopyMode(false);
    setFormAccount(detail);
    setIsFormOpen(true);
  }, [resolveSelectedDetail]);

  const handleOpenCopy = useCallback(async () => {
    const detail = await resolveSelectedDetail();
    if (!detail) return;

    setFormMode('add');
    setIsCopyMode(true);
    setFormAccount(detail);
    setIsFormOpen(true);
  }, [resolveSelectedDetail]);

  const handleCloseForm = useCallback(() => {
    setIsFormOpen(false);
    setIsCopyMode(false);
    setFormAccount(null);
  }, []);

  const handleRequestDelete = useCallback(() => {
    if (!selectedRow?.uuid) return;
    setIsDeleteOpen(true);
  }, [selectedRow?.uuid]);

  const handleCloseDelete = useCallback(() => {
    setIsDeleteOpen(false);
  }, []);

  const handleOpenBulkDelete = useCallback(() => {
    if (filteredRows.length === 0) {
      return;
    }

    setIsBulkDeleteOpen(true);
  }, [filteredRows.length]);

  const handleCloseBulkDelete = useCallback(() => {
    setIsBulkDeleteOpen(false);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    const targetUuid = selectedRow?.uuid;
    if (!targetUuid) return;

    setIsDeleting(true);
    try {
      await handleDeleteByUuid(targetUuid);
      await refreshData();
      clearSelection();
      setIsDeleteOpen(false);
      toast({
        description: 'Xóa tài khoản thành công.'
      });
    } catch (error) {
      const description = error instanceof Error ? error.message : 'Không thể xóa tài khoản. Vui lòng thử lại.';
      toast({
        description,
        variant: 'destructive'
      });
    } finally {
      setIsDeleting(false);
    }
  }, [clearSelection, handleDeleteByUuid, refreshData, selectedRow?.uuid]);

  const handleBulkDelete = useCallback(
    async (uuids: string[]) => {
      if (uuids.length === 0) {
        return;
      }

      const results = await Promise.allSettled(uuids.map(handleDeleteByUuid));
      const successCount = results.filter(result => result.status === 'fulfilled').length;
      const failedCount = uuids.length - successCount;

      await refreshData();
      clearSelection();

      if (failedCount === 0) {
        toast({
          description: `Đã xóa ${successCount} tài khoản đã chọn.`
        });
        return;
      }

      if (successCount > 0) {
        toast({
          description: `Đã xóa ${successCount}/${uuids.length} tài khoản. ${failedCount} tài khoản chưa xóa được.`
        });
        return;
      }

      throw new Error('Không thể xóa các tài khoản đã chọn. Vui lòng thử lại.');
    },
    [clearSelection, handleDeleteByUuid, refreshData]
  );

  const handleRefresh = useCallback(async () => {
    await refreshData();
  }, [refreshData]);

  const handleFormSuccess = useCallback(async () => {
    await refreshData();
    setIsFormOpen(false);
    setIsCopyMode(false);

    if (!selectedRow?.uuid) {
      setFormAccount(null);
      return;
    }

    const refreshedDetail = await fetchDetail(selectedRow.uuid);
    if (!Array.isArray(refreshedDetail)) {
      setFormAccount(refreshedDetail);
    }
  }, [fetchDetail, refreshData, selectedRow?.uuid]);

  const handleImportBodies = useCallback(
    async (bodies: ImportBody[]) => {
      try {
        for (const body of bodies) {
          await addAccount(body as AccountModelInput);
        }
        await refreshData();
        toast({
          description: 'Đã nhập danh mục tài khoản từ Excel thành công',
          variant: 'default'
        });
      } catch (error) {
        console.error('Error importing accounts from Excel:', error);
        toast({
          description: 'Có lỗi xảy ra khi nhập dữ liệu từ Excel',
          variant: 'destructive'
        });
        throw error;
      }
    },
    [addAccount, refreshData]
  );

  const handleImportFromExcel = useCallback(
    async (data: { errors?: string; file: File | null; sheetIndex?: string }) => {
      if (!data.file) {
        toast({
          description: 'Vui lòng chọn file Excel',
          variant: 'destructive'
        });
        return;
      }

      const maxErrors = parseInt(data.errors || '200', 10);
      const sheetIndex = parseInt(data.sheetIndex || '0', 10);

      const result = await processFile({
        file: data.file,
        sheetIndex,
        maxErrors,
        getUUIDByCode,
        context: {}
      });

      if (result.success && result.data) {
        await handleImportBodies(result.data);
        setIsImportDialogOpen(false);
      } else if (result.errors && result.columnMapping) {
        setErrorData({ errors: result.errors, columnMapping: result.columnMapping });
        setShowErrorDialog(true);
        setIsImportDialogOpen(false);
      }
    },
    [getUUIDByCode, handleImportBodies, processFile]
  );

  const handleOpenImportExcel = useCallback(() => {
    const getPermission = (action: 'create' | 'update' | 'delete') => {
      if (isAdmin) return true;
      const resourceKey = getPermissionResourceKeyFromPath(window.location.pathname);
      return resourceKey ? permissions[resourceKey]?.[action] === true : false;
    };

    if (!getPermission('create')) {
      hotToast.error('Bạn chưa được phân quyền để thực hiện tính năng này');
      return;
    }
    setIsImportDialogOpen(true);
  }, [permissions, isAdmin]);

  const value = useMemo<PageContextValue>(
    () => ({
      isLoading,
      rows,
      filteredRows,
      paginatedRows,
      activeGroup,
      selectedRowId,
      selectedRow,
      currentPage,
      pageSize: ACCOUNT_PAGE_SIZE,
      totalItems,
      formMode,
      isFormOpen,
      isDeleteOpen,
      isBulkDeleteOpen,
      isDeleting,
      isCopyMode,
      formAccount,
      isRowActionDisabled,
      isDeleteActionDisabled,
      handleGroupChange,
      handlePageChange,
      handleRowClick,
      handleRefresh,
      handleOpenAdd,
      handleOpenView,
      handleOpenEdit,
      handleOpenCopy,
      handleRequestDelete,
      handleCloseDelete,
      handleConfirmDelete,
      handleOpenBulkDelete,
      handleCloseBulkDelete,
      handleDeleteByUuid,
      handleBulkDelete,
      handleCloseForm,
      handleFormSuccess,
      isImportDialogOpen,
      setIsImportDialogOpen,
      showErrorDialog,
      setShowErrorDialog,
      errorData,
      handleImportFromExcel,
      handleOpenImportExcel
    }),
    [
      activeGroup,
      currentPage,
      filteredRows,
      formAccount,
      formMode,
      handleBulkDelete,
      handleCloseBulkDelete,
      handleCloseDelete,
      handleCloseForm,
      handleConfirmDelete,
      handleDeleteByUuid,
      handleFormSuccess,
      handleGroupChange,
      handleOpenAdd,
      handleOpenBulkDelete,
      handleOpenCopy,
      handleOpenEdit,
      handleOpenView,
      handlePageChange,
      handleRefresh,
      handleRequestDelete,
      handleRowClick,
      isBulkDeleteOpen,
      isCopyMode,
      isDeleteActionDisabled,
      isDeleteOpen,
      isDeleting,
      isFormOpen,
      isLoading,
      isRowActionDisabled,
      paginatedRows,
      rows,
      selectedRow,
      selectedRowId,
      totalItems,
      isImportDialogOpen,
      showErrorDialog,
      errorData,
      handleImportFromExcel,
      handleOpenImportExcel
    ]
  );

  return <AccountPageContext.Provider value={value}>{children}</AccountPageContext.Provider>;
}

export const useAccountPageContext = () => {
  const context = useContext(AccountPageContext);
  if (!context) {
    throw new Error('useAccountPageContext must be used within AccountPageProvider');
  }

  return context;
};
